import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import SegmentIntrospectResultDTO = App.Data.Segments.SegmentIntrospectResultDTO;
import SegmentDO = App.Data.Segments.SegmentDO;
import DocumentVerticalDTO = App.Data.DocumentVerticalDTO;

type segmentBuilderState = {
	segment: SegmentDO;
	introspectResult: SegmentIntrospectResultDTO[];
	totalSegmentBase: number;
	supportedDataSourcesInSegmentBuilder: string[];
	availableMetrics: DocumentVerticalDTO[];
	segmentHasChanges: boolean;
	circularSegmentDependencyDetected: boolean;
	introspectErrorDetected: boolean;
};

export const useSegmentBuilderStore = defineStore("segmentBuilderStore", () => {
	function createSegmentBuilderStore(): segmentBuilderState {
		return {
			segment: null,
			introspectResult: [],
			totalSegmentBase: 0,
			supportedDataSourcesInSegmentBuilder: [
				"segment",
				"tab_occ_prk_v",
				"tab_cus_v",
			],
			availableMetrics: [],
			segmentHasChanges: false,
			circularSegmentDependencyDetected: false,
			introspectErrorDetected: false,
		};
	}

	const segmentBuilderState = ref(createSegmentBuilderStore());

	watch(
		() => segmentBuilderState.value?.segment,
		(value, oldValue) => {
			segmentBuilderState.value.segmentHasChanges = oldValue !== null;
			segmentBuilderState.value.circularSegmentDependencyDetected = false;
			segmentBuilderState.value.introspectErrorDetected = false;
		},
		{ deep: true }
	);

	const orderDataPartsBySourceAndType = () => {
		const dataSourceOrder =
			segmentBuilderState.value.supportedDataSourcesInSegmentBuilder;
		const typeOrder = ["include", "exclude"];

		segmentBuilderState.value.segment?.parts.sort((a, b) => {
			const dataSourceComparison =
				dataSourceOrder.indexOf(a.data_source) -
				dataSourceOrder.indexOf(b.data_source);

			if (dataSourceComparison !== 0) {
				return dataSourceComparison;
			}

			return typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
		});
	};

	watch(
		() => segmentBuilderState.value.segment?.parts,
		() => {
			orderDataPartsBySourceAndType();
		},
		{ deep: true }
	);

	const allActiveFilters = computed(() => {
		return segmentBuilderState.value.segment?.parts
			?.filter((part) => part.active_filters && part.active_filters.length > 0)
			.flatMap((part) => part.active_filters);
	});

	const getDataSourceForFilter = (filter: ActiveFilterDTO): string => {
		const { available_for_data_source: dataSources } = filter.dimension;

		if (
			filter.dimension.type === "datetime" &&
			dataSources.includes("tab_occ_prk_v")
		) {
			return "tab_occ_prk_v";
		}

		return dataSources.includes("segment")
			? "segment"
			: dataSources.includes("tab_cus_v")
			? "tab_cus_v"
			: dataSources.includes("tab_occ_prk_v")
			? "tab_occ_prk_v"
			: "unknown";
	};

	const resetState = () => {
		segmentBuilderState.value = createSegmentBuilderStore();
	};

	return {
		segmentBuilderState,
		allActiveFilters,
		getDataSourceForFilter,
		resetState,
	};
});
